<script>
import { mapGetters } from 'vuex';
import AccountStorageAPI from '@/api/accountStorage';

export default {
  name: 'StorageQuotaCard',

  components: {
    StorageQuotaEditForm: () => import('./StorageQuotaEditForm.vue'),
  },

  data() {
    return {
      loading: false,
      showEditModal: false,
      quotaInfo: {
        total: 0,
        used: 0,
        balance: 0,
        usage_percentage: 0,
      },
    };
  },

  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),

    canManage() {
      return this.currentUser && this.currentUser.type === 'Administrator';
    },

    usagePercentage() {
      return this.quotaInfo.usage_percentage.toFixed(2);
    },

    currentUsageHuman() {
      return this.formatBytes(this.quotaInfo.used);
    },

    quotaLimitHuman() {
      return this.formatBytes(this.quotaInfo.total);
    },

    remainingQuotaHuman() {
      return this.formatBytes(this.quotaInfo.balance);
    },

    progressClass() {
      const percentage = this.quotaInfo.usage_percentage;
      if (percentage >= 90) return 'danger';
      if (percentage >= 70) return 'warning';
      return 'success';
    },

    isNearLimit() {
      return this.quotaInfo.usage_percentage >= 80;
    },
  },

  mounted() {
    this.fetchQuotaInfo();
  },

  methods: {
    async fetchQuotaInfo() {
      this.loading = true;
      try {
        const response = await AccountStorageAPI.show(this.currentAccountId);
        this.quotaInfo = response.data;
      } catch (error) {
        this.showAlert(this.$t('ACCOUNT_STORAGE.FETCH_ERROR'));
      } finally {
        this.loading = false;
      }
    },

    async handleUpdateQuota(newLimit) {
      try {
        await AccountStorageAPI.update(this.currentAccountId, {
          total: newLimit,
        });
        this.showAlert(this.$t('ACCOUNT_STORAGE.UPDATE_SUCCESS'), 'success');
        this.showEditModal = false;
        this.fetchQuotaInfo();
      } catch (error) {
        this.showAlert(
          error.message || this.$t('ACCOUNT_STORAGE.UPDATE_ERROR')
        );
      }
    },

    formatBytes(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / k ** i) * 100) / 100 + ' ' + sizes[i];
    },

    showAlert(message, type = 'error') {
      bus.$emit('newToastMessage', {
        message,
        type,
      });
    },
  },
};
</script>

<template>
  <div class="storage-quota-card">
    <woot-loading-state v-if="loading" />

    <div v-else class="quota-content">
      <!-- 标题 -->
      <div class="quota-header">
        <h3>{{ $t('ACCOUNT_STORAGE.TITLE') }}</h3>
        <woot-button
          v-if="canManage"
          icon="edit"
          variant="smooth"
          size="tiny"
          @click="showEditModal = true"
        >
          {{ $t('ACCOUNT_STORAGE.UPDATE_QUOTA') }}
        </woot-button>
      </div>

      <!-- 使用进度条 -->
      <div class="quota-progress">
        <div class="progress-label">
          <span>{{ $t('ACCOUNT_STORAGE.CURRENT_USAGE') }}</span>
          <span>{{ usagePercentage }}%</span>
        </div>
        <div class="progress-bar">
          <div
            class="progress-fill"
            :class="progressClass"
            :style="{ width: usagePercentage + '%' }"
          />
        </div>
        <div class="progress-details">
          <span>{{ currentUsageHuman }} / {{ quotaLimitHuman }}</span>
          <span
            >{{ $t('ACCOUNT_STORAGE.REMAINING') }}:
            {{ remainingQuotaHuman }}</span
          >
        </div>
      </div>

      <!-- 警告信息 -->
      <woot-alert
        v-if="isNearLimit"
        type="warning"
        :message="$t('ACCOUNT_STORAGE.NEAR_LIMIT_WARNING')"
      />
    </div>

    <!-- 编辑配额弹窗 -->
    <woot-modal
      v-if="showEditModal"
      :show="showEditModal"
      :header-title="$t('ACCOUNT_STORAGE.UPDATE_QUOTA')"
      @close="showEditModal = false"
    >
      <StorageQuotaEditForm
        :current-limit="quotaInfo.total"
        @submit="handleUpdateQuota"
        @cancel="showEditModal = false"
      />
    </woot-modal>
  </div>
</template>

<style lang="scss" scoped>
.storage-quota-card {
  @apply bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm;
}

.quota-header {
  @apply flex justify-between items-center mb-4;

  h3 {
    @apply text-lg font-semibold text-slate-800 dark:text-slate-100;
  }
}

.quota-progress {
  @apply mb-4;
}

.progress-label {
  @apply flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2;
}

.progress-bar {
  @apply h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden;
}

.progress-fill {
  @apply h-full transition-all duration-300;

  &.success {
    @apply bg-green-500;
  }

  &.warning {
    @apply bg-yellow-500;
  }

  &.danger {
    @apply bg-red-500;
  }
}

.progress-details {
  @apply flex justify-between text-sm text-slate-600 dark:text-slate-400 mt-2;
}

.quota-stats {
  @apply flex gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg;
}

.stat-item {
  @apply flex flex-col;
}

.stat-label {
  @apply text-sm text-slate-600 dark:text-slate-400;
}

.stat-value {
  @apply text-xl font-semibold text-slate-800 dark:text-slate-100;
}
</style>
